import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ERROR_MESSAGES } from '@/constants/errorMessages';
import {
  ALLOWED_FILE_TYPES,
  FILE_CONSTRAINTS,
  getMaxFileSize,
  getMaxFileSizeMB,
} from '@/constants/fileTypes';
import { useToaster } from '@/hooks/useToaster';
import type {
  AttachmentMimeType,
  AttachmentSource,
  OutgoingFile,
} from '@/services/conversations/thread';
import {
  convertHeicAsset,
  type HeicConvertibleAsset,
  needsHeicConversion,
} from '@/util/convertHeicAsset';
import { Sentry } from '@/util/sentry';

type PickedAsset = HeicConvertibleAsset;

function isAllowedType(mimeType: string): mimeType is AttachmentMimeType {
  return ALLOWED_FILE_TYPES.includes(mimeType);
}

/** A name for a photo the library handed over without one, which the server needs to store it. */
function photoName(mimeType: string): string {
  const extension = mimeType.split('/')[1] === 'jpeg' ? 'jpg' : mimeType.split('/')[1];

  return `photo-${Date.now()}.${extension}`;
}

/**
 * Opens the camera, the photo library or the file browser and returns what was picked as
 * an attachment, or nothing when the reader cancels or the file cannot be
 * sent. Refusals are explained in a toast here, so callers only add what
 * comes back.
 *
 * The limits are the inquiry chat's: the same types, the same size, and three
 * files per message, which the server enforces too.
 */
export function useSupportFilePicker() {
  const toast = useToaster();

  const accept = useCallback(
    (
      asset: PickedAsset,
      current: readonly OutgoingFile[],
      source: AttachmentSource
    ): OutgoingFile | null => {
      const mimeType = asset.mimeType ?? asset.file?.type ?? '';

      if (!isAllowedType(mimeType)) {
        toast.showError({
          title: 'That file type can’t be sent',
          message: 'Send an image, PDF, Word, Excel, CSV or video file.',
        });

        return null;
      }

      const size = asset.size ?? asset.file?.size ?? 0;

      if (size > getMaxFileSize(mimeType)) {
        toast.showError({ title: `File too large (max ${getMaxFileSizeMB(mimeType)}MB)` });

        return null;
      }

      const name = asset.name || asset.file?.name || photoName(mimeType);

      if (current.some((file) => file.name === name && file.size === size)) {
        toast.showError({ title: 'File already added' });

        return null;
      }

      return { id: uuidv4(), name, mimeType, uri: asset.uri, size, source, file: asset.file };
    },
    [toast]
  );

  /**
   * A HEIC photo is decoded in full before it can be sent as JPEG, so one over the limit is
   * refused first. Otherwise the reader waits through the decode only to be told it was too big.
   */
  const prepare = useCallback(
    async (asset: PickedAsset): Promise<PickedAsset | null> => {
      const size = asset.size ?? asset.file?.size ?? 0;

      if (size > getMaxFileSize('image/heic') && (await needsHeicConversion(asset))) {
        toast.showError({ title: `File too large (max ${getMaxFileSizeMB('image/heic')}MB)` });

        return null;
      }

      return convertHeicAsset(asset);
    },
    [toast]
  );

  const hasRoom = useCallback(
    (current: readonly OutgoingFile[]) => {
      if (current.length < FILE_CONSTRAINTS.MAX_FILE_COUNT) {
        return true;
      }

      toast.showError({
        title: `Maximum ${FILE_CONSTRAINTS.MAX_FILE_COUNT} files allowed`,
        message: ERROR_MESSAGES.ATTACHMENTS.LIMIT_EXCEEDED.CHAT,
      });

      return false;
    },
    [toast]
  );

  const reportFailure = useCallback(
    (error: unknown, source: 'camera' | 'photo' | 'file') => {
      Sentry.captureException(error, {
        tags: { component: 'useSupportFilePicker', source },
        extra: { errorMessage: error instanceof Error ? error.message : undefined },
      });

      toast.showError({ title: 'Couldn’t add that file' });
    },
    [toast]
  );

  /**
   * Photos are requested in the compatible representation so iOS hands over a JPEG instead of
   * the original HEIC, which Android and Windows cannot draw. The picker's default keeps the
   * original bytes whatever the quality, so this must stay set.
   *
   * The library needs no permission on iOS or Android, since the system picker
   * hands over only what the reader chooses. The camera does, and a refusal is
   * explained rather than ignored, because the reader otherwise sees nothing
   * happen.
   */
  const pickImage = useCallback(
    async (
      source: 'camera' | 'photo',
      current: readonly OutgoingFile[]
    ): Promise<OutgoingFile | null> => {
      if (!hasRoom(current)) {
        return null;
      }

      try {
        if (source === 'camera') {
          const permission = await ImagePicker.requestCameraPermissionsAsync();

          if (!permission.granted) {
            toast.showError({
              title: 'Camera access is off',
              message: 'Allow camera access for Primum in Settings to take a photo.',
            });

            return null;
          }
        }

        const options: ImagePicker.ImagePickerOptions = {
          mediaTypes: ['images'],
          quality: 0.8,
          preferredAssetRepresentationMode:
            ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
        };

        const result =
          source === 'camera'
            ? await ImagePicker.launchCameraAsync(options)
            : await ImagePicker.launchImageLibraryAsync(options);

        const asset = result.assets?.[0];

        if (result.canceled || !asset) {
          return null;
        }

        const prepared = await prepare({
          name: asset.fileName,
          mimeType: asset.mimeType ?? (source === 'camera' ? 'image/jpeg' : undefined),
          uri: asset.uri,
          size: asset.fileSize,
          file: asset.file,
        });

        return prepared
          ? accept(prepared, current, source === 'camera' ? 'camera' : 'library')
          : null;
      } catch (error) {
        reportFailure(error, source);

        return null;
      }
    },
    [accept, hasRoom, prepare, reportFailure, toast]
  );

  const pickFile = useCallback(
    async (current: readonly OutgoingFile[]): Promise<OutgoingFile | null> => {
      if (!hasRoom(current)) {
        return null;
      }

      try {
        const result = await DocumentPicker.getDocumentAsync({
          multiple: false,
          type: ALLOWED_FILE_TYPES,
        });

        const asset = result.assets?.[0];

        if (result.canceled || !asset) {
          return null;
        }

        const prepared = await prepare({
          name: asset.name,
          mimeType: asset.mimeType,
          uri: asset.uri,
          size: asset.size,
          file: asset.file,
        });

        return prepared ? accept(prepared, current, 'files') : null;
      } catch (error) {
        reportFailure(error, 'file');

        return null;
      }
    },
    [accept, hasRoom, prepare, reportFailure]
  );

  const takePhoto = useCallback(
    (current: readonly OutgoingFile[]) => pickImage('camera', current),
    [pickImage]
  );

  const pickPhoto = useCallback(
    (current: readonly OutgoingFile[]) => pickImage('photo', current),
    [pickImage]
  );

  return { takePhoto, pickPhoto, pickFile };
}
